import { atom, selector } from "recoil";
import React from "react";
import moment from "moment";
import { pageState } from "../../../state/GlobalState";
import { permissionState } from "../../../api/state/AuthState";
import {
    CASE_FILE_TYPE_PILL,
    caseFileTypePillBase,
    getCaseStatusPillTheme,
} from "../caseFiles/state";
import { CaseFileWarningsIcon } from "../caseFiles/CaseFileRemarksWarnings";
import {
    getCaseFileOperationalWarnings,
    normalizeBagsFromFormAndApi,
} from "../caseFiles/caseFileOperationalWarnings";

export const caseReportListState = atom({
    key: 'caseReportListState',
    default: [],
})

export const caseReportAirlineState = atom({
    key: 'caseReportAirlineState',
    default: [],
})

export const filtersDefault = [
    {
        label: 'Period',
        size: 3,
        required: true,
        type: 'dateRange',
        value: [moment().subtract(1, 'week').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
    },
    {
        label: 'Type',
        size: 2,
        required: true,
        type: 'select',
        value: { label: 'All Types', value: 'All' },
        list: [
            { label: 'All Types', value: 'All' },
            { label: 'AHL', value: 'AHL' },
            { label: 'DPR', value: 'DPR' },
            { label: 'OHD', value: 'OHD' },
            { label: 'UNCLAIMED', value: 'UNCLAIMED' },
            { label: 'CLAIMS', value: 'CLAIMS' },
        ]
    },
    {
        label: 'Airline',
        size: 2,
        required: true,
        type: 'select',
        value: { label: 'All Airlines', value: 'All' },
        list: [
            { label: 'All Airlines', value: 'All' },
        ]
    },
    {
        label: 'Status',
        size: 2,
        required: true,
        type: 'select',
        value: { label: 'All Status', value: 'All' },
        list: [
            { label: 'All Status', value: 'All' },
            { label: 'TRACING', value: 'TRACING' },
            { label: 'LOCATED', value: 'LOCATED' },
            { label: 'PARTIALLY LOCATED', value: 'PARTIALLY LOCATED' },
            { label: 'RECEIVED', value: 'RECEIVED' },
            { label: 'PARTIALLY RECEIVED', value: 'PARTIALLY RECEIVED' },
            { label: 'DELIVERED', value: 'DELIVERED' },
            { label: 'PARTIALLY DELIVERED', value: 'PARTIALLY DELIVERED' },
            { label: 'FORWARDED', value: 'FORWARDED' },
            { label: 'PARTIALLY FORWARDED', value: 'PARTIALLY FORWARDED' },
            { label: 'FORWARDED/DELIVERED', value: 'FORWARDED/DELIVERED' },
        ]
    },
    {
        label: 'Created by staff',
        name: 'CreatedByUserId',
        size: 3,
        required: false,
        type: 'select',
        value: { label: 'All staff', value: 'All' },
        list: [
            { label: 'All staff', value: 'All' },
        ]
    },
]

export const caseReportDataStructureState = selector({
    key: 'caseReportDataStructureState',
    get: ({ get }) => (
        [

            { 
                label: 'Reference',
                field: 'primary_reference',
                type: 'text',
                order: { table: 2 },
                table: { 
                    minWidth: 150,
                    maxWidth: 200,
                    customRender: (value, params) => {
                        const fileRef =
                            params.data.file_reference != null &&
                            String(params.data.file_reference).trim() !== ""
                                ? String(params.data.file_reference).trim()
                                : null;
                        const ref =
                            params.data.reference != null &&
                            String(params.data.reference).trim() !== ""
                                ? String(params.data.reference).trim()
                                : null;
                        return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                gap: "3px",
                                lineHeight: 1.3,
                                marginTop: "7px",
                                maxWidth: "200px",
                                minHeight: 0,
                            }}
                        >
                                <span
                                    style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={fileRef || undefined}
                                >
                                    {fileRef || "—"}
                                </span>
                                <span
                                    style={{
                                        fontSize: "0.6875rem",
                                        lineHeight: 1.2,
                                        color: "#6b7280",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={ref || undefined}
                                >
                                    {ref || "—"}
                                </span>
                        </div>
                        );
                    },	
                },
            },
            { 
                label: 'Flight',
                type: 'text',
                order: { table: 3 },
                table: {
                    minWidth: 120,
                    maxWidth: 120,
                    customRender: (value, params) => {
                        const flight = params.data.flight;
                        const flightLine = flight != null && flight !== '' ? String(flight) : '—';
                        const dateRaw = params.data.flight_date;
                        const dateLine = dateRaw ? moment(dateRaw).format('DD-MMM-YY') : '—';
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    gap: "2px",
                                    lineHeight: 1.3,
                                    marginTop: "7px",
                                    minHeight: 0,
                                }}
                            >
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {flightLine}
                                </span>
                                <span
                                    style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontSize: "0.8125rem",
                                    }}
                                >
                                    {dateLine}
                                </span>
                            </div>
                        );
                    },
                },
            },
            { 
                label: 'Case Status',
                tableHeader: 'Status',
                type: 'text',
                order: { table: 4.5 },
                table: {
                    minWidth: 250,
                    maxWidth: 250,
                    customRender: (value, params) => {
                        const status =
                            params.data.case_status != null && params.data.case_status !== ""
                                ? String(params.data.case_status)
                                : "—";
                        const rec = Number(params.data.number_of_received_bags ?? 0);
                        const tot = Number(params.data.number_of_bags ?? 0);
                        const ratio = Number.isFinite(tot) ? `${rec}/${tot}` : "—";
                        const closed = !!params.data.closed;
                        const pillTheme = getCaseStatusPillTheme(status === "—" ? "" : status);
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    gap: "3px",
                                    lineHeight: 1.3,
                                    marginTop: "7px",
                                    minHeight: 0,
                                    maxWidth: "100%",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        minWidth: 0,
                                        alignSelf: "flex-start",
                                        maxWidth: "100%",
                                    }}
                                >
                                    {closed ? (
                                        <i
                                            className="mdi mdi-lock-outline"
                                            title="Closed case"
                                            aria-hidden
                                            style={{
                                                fontSize: "15px",
                                                color: "#6b7280",
                                                flexShrink: 0,
                                                lineHeight: 1,
                                            }}
                                        />
                                    ) : null}
                                    <span
                                        style={{
                                            ...caseFileTypePillBase,
                                            fontSize: "0.8125rem",
                                            fontWeight: 700,
                                            letterSpacing: "0.03em",
                                            padding: "3px 10px",
                                            ...pillTheme,
                                            flex: "0 1 auto",
                                            width: "fit-content",
                                            maxWidth: "100%",
                                        }}
                                        title={status}
                                    >
                                        {status}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        fontSize: "0.6875rem",
                                        lineHeight: 1.2,
                                        color: "#6b7280",
                                        whiteSpace: "nowrap",
                                    }}
                                    title="Received bags / total bags"
                                >
                                    <i
                                        className="mdi mdi-bag-suitcase-outline"
                                        aria-hidden
                                        style={{
                                            fontSize: "0.875rem",
                                            color: "#6b7280",
                                            lineHeight: 1,
                                            opacity: 0.9,
                                        }}
                                    />
                                    <span>{ratio} rcv</span>
                                </div>
                            </div>
                        );
                    },
                },
            },
            { 
                label: 'Name',
				field: 'full_name',
                type: 'text',
                order: { table: 5 },
                table: {
                    minWidth: 160,
                    maxWidth: 300,
                    customRender: (value, params) => {
                        const nameLine =
                            params.data.full_name != null && params.data.full_name !== ''
                                ? String(params.data.full_name)
                                : '—';
                        const addr = params.data.address;
                        let addressLine = '—';
                        if (addr != null) {
                            if (typeof addr === 'object') {
                                const label = addr.name ?? addr.label ?? '';
                                addressLine = label !== '' ? String(label) : '—';
                            } else {
                                addressLine = String(addr);
                            }
                        }
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    gap: '2px',
                                    lineHeight: 1.3,
                                    marginTop: "7px",
                                    minHeight: 0,
                                }}
                            >
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {nameLine}
                                </span>
                                <span
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.8125rem',
                                    }}
                                >
                                    {addressLine}
                                </span>
                            </div>
                        );
                    },
                },
            },
            {
                label: 'Remarks',
                type: 'textarea',
                order: { table: 6 },
                table: {
                    flex: 1,
                    minWidth: 140,
                    customRender: (value, params) => {
                        const raw = params.data.remarks;
                        const text =
                            raw != null && String(raw).trim() !== "" ? String(raw).trim() : "—";
                        const caseType = params.data["type"] ?? "";
                        const typePillTheme = CASE_FILE_TYPE_PILL[caseType] || {
                            background: "#f3f4f6",
                            color: "#6b7280",
                            border: "1px solid #e5e7eb",
                        };
                        const rowWarnings = getCaseFileOperationalWarnings({
                            type: params.data?.type,
                            departureDate: params.data?.departure_date,
                            flightDate: params.data?.flight_date,
                            closed: params.data?.closed,
                            caseStatus: params.data?.case_status,
                            bags: normalizeBagsFromFormAndApi(null, params.data?.bags),
                        });
                        const isCourtesy = Boolean(params.data?.courtesy);
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    gap: "4px",
                                    marginTop: "7px",
                                    maxWidth: "100%",
                                    minHeight: 0,
                                    lineHeight: 1.35,
                                }}
                            >
                                <span
                                    style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        minWidth: 0,
                                    }}
                                    title={text === "—" ? undefined : text}
                                >
                                    {text}
                                </span>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: "6px",
                                        minWidth: 0,
                                    }}
                                >
                                    <span
                                        style={{
                                            ...caseFileTypePillBase,
                                            ...typePillTheme,
                                            alignSelf: "center",
                                        }}
                                        title={caseType || undefined}
                                    >
                                        {caseType || "—"}
                                    </span>
                                    {isCourtesy ? (
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxSizing: "border-box",
                                                minWidth: 24,
                                                minHeight: 24,
                                                padding: "0 6px",
                                                borderRadius: 999,
                                                background: "#ecfdf5",
                                                color: "#047857",
                                                border: "1px solid #a7f3d0",
                                                lineHeight: 1,
                                                alignSelf: "center",
                                            }}
                                            title="Courtesy"
                                            aria-label="Courtesy"
                                        >
                                            <i
                                                className="mdi mdi-handshake-outline"
                                                style={{ fontSize: "0.9rem", lineHeight: 1 }}
                                                aria-hidden
                                            />
                                        </span>
                                    ) : null}
                                    <CaseFileWarningsIcon warnings={rowWarnings} compact />
                                </div>
                            </div>
                        );
                    },
                },
            },

        ].filter((v)=>{
            const page = get(pageState)
            const permissions = get(permissionState)
            if(! v.permission){
                return true
            }
            let absolute = false;
            if(v.permission.substr(0,1) === ":"){
                absolute = true;
                v.permission = v.permission.substr(1)
            }
            v.permission = absolute ? v.permission : v.permission + "-" + page
            return permissions?.includes(v.permission)
        })
    )
})
